import { CaptionText } from '@/components/ui/Typography'
import { BRAND_COLORS } from '@/lib/brandColors'
import React, { useMemo, useState } from 'react'
import { Linking, Platform, Pressable } from 'react-native'
import RenderHTML, {
  type MixedStyleRecord,
  type RenderersProps,
} from 'react-native-render-html'
import { useMedia, YStack } from 'tamagui'

const SIZE = {
  sm: { fontSize: 14, lineHeight: 18 },
  md: { fontSize: 16, lineHeight: 22 },
} as const

const BLOCK_GAP = 12

const FONT = Platform.OS === 'web' ? 'Inter, sans-serif' : 'InterRegular'
const FONT_BOLD = Platform.OS === 'web' ? 'Inter, sans-serif' : 'InterBold'
const FONT_SEMI = Platform.OS === 'web' ? 'Inter, sans-serif' : 'InterSemiBold'

const renderersProps: Partial<RenderersProps> = {
  a: {
    onPress: (_e, href) => {
      if (href) void Linking.openURL(href)
    },
  },
}

const VOID = new Set(['br', 'hr', 'img'])

function truncateHtml(html: string, maxChars: number): { html: string; truncated: boolean } {
  const src = html.trim()
  if (!src) return { html: '', truncated: false }

  let i = 0
  let n = 0
  let out = ''
  const stack: string[] = []

  const closeAll = () => {
    for (let k = stack.length - 1; k >= 0; k--) out += `</${stack[k]}>`
  }

  while (i < src.length) {
    if (src[i] === '<') {
      const gt = src.indexOf('>', i)
      if (gt === -1) break
      const raw = src.slice(i + 1, gt).trim()
      const isClose = raw.startsWith('/')
      const name = (isClose ? raw.slice(1) : raw).split(/[\s/]/)[0].toLowerCase()
      if (isClose) {
        const idx = stack.lastIndexOf(name)
        if (idx !== -1) stack.splice(idx, 1)
      } else if (!raw.endsWith('/') && !VOID.has(name)) {
        stack.push(name)
      }
      out += src.slice(i, gt + 1)
      i = gt + 1
      continue
    }

    const next = src.indexOf('<', i)
    const end = next === -1 ? src.length : next
    const chunk = src.slice(i, end)

    if (n + chunk.length <= maxChars) {
      out += chunk
      n += chunk.length
      i = end
      continue
    }

    let slice = chunk.slice(0, maxChars - n)
    const sp = slice.lastIndexOf(' ')
    if (sp > slice.length * 0.6) slice = slice.slice(0, sp)
    out += `${slice.trimEnd()}…`
    closeAll()
    return { html: out, truncated: true }
  }

  return { html: out, truncated: false }
}

type Props = {
  html: string
  numberOfLines: number
}

export function HtmlTruncatedReadMore({ html, numberOfLines }: Props) {
  const media = useMedia()
  const size = media.gtMd ? SIZE.md : SIZE.sm
  const fullHtml = html.trim()
  const [expanded, setExpanded] = useState(false)

  const preview = useMemo(
    () => truncateHtml(fullHtml, numberOfLines * 52),
    [fullHtml, numberOfLines],
  )

  const tagsStyles = useMemo<MixedStyleRecord>(() => {
    const block = {
      marginTop: 0,
      marginBottom: BLOCK_GAP,
      fontSize: size.fontSize,
      lineHeight: size.lineHeight,
      fontFamily: FONT,
      color: BRAND_COLORS.text,
    }
    const heading = {
      ...block,
      marginBottom: 8,
      fontFamily: FONT_BOLD,
      fontWeight: '700' as const,
    }
    return {
      body: { ...block, marginBottom: 0 },
      p: block,
      h1: heading,
      h2: heading,
      h3: heading,
      h4: heading,
      h5: heading,
      h6: heading,
      strong: { fontWeight: '700', fontFamily: FONT_BOLD },
      b: { fontWeight: '700', fontFamily: FONT_BOLD },
      a: {
        color: BRAND_COLORS.linkAccent,
        fontWeight: '600',
        fontFamily: FONT_SEMI,
        textDecorationLine: 'underline',
        textDecorationColor: BRAND_COLORS.linkAccent,
      },
    }
  }, [size])

  if (!fullHtml) return null

  const isWeb = Platform.OS === 'web'
  const sourceHtml = isWeb || expanded ? fullHtml : preview.html

  return (
    <YStack width="100%" gap={4} marginBottom={-BLOCK_GAP}>
      <RenderHTML
        source={{ html: sourceHtml }}
        contentWidth={320}
        tagsStyles={tagsStyles}
        systemFonts={['Inter', 'InterRegular', 'InterSemiBold', 'InterBold', 'System']}
        renderersProps={renderersProps}
        defaultTextProps={{ selectable: true }}
      />
      {!isWeb && preview.truncated ? (
        <Pressable
          onPress={() => setExpanded((v) => !v)}
          hitSlop={8}
          style={{ alignSelf: 'flex-end' }}
        >
          <CaptionText color={BRAND_COLORS.linkAccent} cursor="pointer">
            {expanded ? 'Read less' : 'Read more'}
          </CaptionText>
        </Pressable>
      ) : null}
    </YStack>
  )
}
